/*
  Behaviour: Disclosure — dismissal
  Responsibility: shut an open <details> when the pointer lands outside it, or when Escape is
                  pressed. Nothing else. What the panel contains, and what opening it means,
                  belong to whichever component opted in.

  Section-agnostic on purpose, and named for the behaviour rather than for the component it
  was first needed by. A component opts in with two hooks and no script of its own:

    <details data-kl-disclosure>
      <summary data-kl-disclosure-summary> ... </summary>

  <details> already gives open and close on the summary, keyboard operation, and a place in
  the tab order. The two things it does not give are these, which is the whole of this file.

  The summary hook is separate because it has a job of its own: on Escape, focus goes back to
  it. Without that a keyboard user is left standing on an element that has just been hidden
  and the next Tab starts again from the top of the document. It is a data attribute rather
  than a `summary` tag selector so that this file selects nothing by tag, and it is guarded —
  a component that leaves it off loses the focus return and keeps the dismissal.

  Both listeners are on the document rather than on each control, and that is load-bearing
  rather than a shortcut. Anything re-rendered through the Section Rendering API — the
  collection grid is the live example — throws away the elements a bound listener was
  sitting on, and the second interaction of a session would do nothing. A delegated listener
  has nothing to rebind.

  Note for whoever touches this next: assets/kl-collection-sort.js carries its own copy of
  the same two handlers, scoped to [data-kl-collection-sort]. That file predates this one and
  is untouched here because it belongs to work in flight. Migrating it is a matter of putting
  the two hooks on its markup and deleting the file.
*/

/*
  Pure: which of the open controls a click landed outside of. DOM-free apart from the
  containment test it is handed, so the decision is testable without a browser.
*/
function getDisclosuresToClose(openDisclosures, isInsideDisclosure) {
  return openDisclosures.filter((disclosure) => !isInsideDisclosure(disclosure));
}

function getOpenDisclosures() {
  return Array.from(document.querySelectorAll('[data-kl-disclosure][open]'));
}

function closeDisclosure(disclosure) {
  disclosure.removeAttribute('open');
}

function handleDocumentClick(event) {
  try {
    const openDisclosures = getOpenDisclosures();

    if (!openDisclosures.length) return;

    const toClose = getDisclosuresToClose(openDisclosures, (disclosure) => disclosure.contains(event.target));

    toClose.forEach(closeDisclosure);
  } catch (error) {
    console.debug('kl-disclosure: could not close on outside click', { error });
  }
}

function handleDocumentKeyUp(event) {
  if (event.key !== 'Escape') return;

  try {
    const openDisclosures = getOpenDisclosures();

    if (!openDisclosures.length) return;

    openDisclosures.forEach((disclosure) => {
      closeDisclosure(disclosure);

      const summary = disclosure.querySelector('[data-kl-disclosure-summary]');

      if (summary) summary.focus();
    });
  } catch (error) {
    console.debug('kl-disclosure: could not close on Escape', { error });
  }
}

document.addEventListener('click', handleDocumentClick);
document.addEventListener('keyup', handleDocumentKeyUp);
